import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame } from "lucide-react";
import { fetchFeatured } from "@/lib/api";
import RestaurantCard from "@/components/RestaurantCard";
import ArticleCard from "@/components/ArticleCard";
import EventCard from "@/components/EventCard";
import FeaturedVideo from "@/components/FeaturedVideo";
import TrendingStrip from "@/components/TrendingStrip";

const TICKER_ITEMS = [
  "Houston Hidden Gems", "Chef Interviews", "Cocktail Classic",
  "BBQ Battles", "Brunch Guide", "International Cuisine",
  "Wine Tastings", "Food Truck Fridays", "Restaurant Makeovers",
  "Celebrity Chefs", "Behind the Kitchen",
];

const FEATURED_CITIES = ["Houston", "Atlanta", "Dallas", "Los Angeles", "New Orleans", "New York", "San Diego", "Golden Triangle", "All"];

export default function Home() {
  const [data, setData] = useState({ restaurants: [], articles: [], events: [] });
  const [loading, setLoading] = useState(true);
  const [city, setCity] = useState("Houston");

  useEffect(() => {
    setLoading(true);
    fetchFeatured(city)
      .then(setData)
      .finally(() => setLoading(false));
  }, [city]);

  const heroArticle = data.articles[0];
  const sideArticles = data.articles.slice(1, 4);

  return (
    <div data-testid="home-page" className="bg-sand-50">
      {/* HERO — Asymmetric editorial split */}
      <section className="relative max-w-7xl mx-auto px-6 md:px-12 pt-12 pb-16 md:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-end">
          <div className="lg:col-span-7 animate-fade-up">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-6 flex items-center gap-3">
              <span className="w-10 h-px bg-brand-500" />
              <span>Issue 01 · 8 Markets · 2026</span>
            </div>
            <h1 className="font-serif font-black text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.92] tracking-tight text-sand-900">
              Where Food,<br />
              <span className="italic font-medium">Culture</span> &<br />
              <span className="text-brand-500">Community</span> Meet.
            </h1>
            <p className="mt-8 max-w-xl text-lg font-light text-sand-700 leading-relaxed">
              The editorial home for chefs, restaurants, and bartenders across America's
              most exciting food cities — Houston, Atlanta, Dallas, Los Angeles, New Orleans,
              New York, San Diego & the Golden Triangle of Southeast Texas. Reviews, chef interviews,
              behind-the-kitchen stories, and the signature events that define the year.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link
                to="/directory"
                data-testid="hero-cta-directory"
                className="group bg-sand-900 text-sand-50 px-7 py-4 font-mono text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-brand-500 transition-colors flex items-center gap-3 active:scale-95"
              >
                Browse the Network <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/articles"
                data-testid="hero-cta-articles"
                className="group px-7 py-4 font-mono text-[11px] font-bold tracking-[0.25em] uppercase text-sand-900 border border-sand-900 hover:bg-sand-900 hover:text-sand-50 transition-colors flex items-center gap-3"
              >
                Read the Stories
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 animate-fade-up" style={{ animationDelay: "200ms" }}>
            <div className="img-zoom aspect-[4/5] relative">
              <img
                src="https://images.pexels.com/photos/18330389/pexels-photo-18330389.jpeg"
                alt="Houston food"
                className="w-full h-full object-cover"
              />
              {/* Brand stamp / seal */}
              <img
                src="https://customer-assets.emergentagent.com/job_tastebuds-hub/artifacts/2vjwnbkm_Tastbuds%20Food%20Network%20Logo.png"
                alt="Tastebuds Food Network"
                data-testid="hero-brand-stamp"
                className="hidden md:block absolute top-4 left-4 w-40 lg:w-48 h-40 lg:h-48 object-contain drop-shadow-2xl rotate-[-6deg] hover:rotate-0 transition-transform duration-700 z-10"
              />
              <div className="absolute -bottom-6 -left-6 bg-brand-500 text-sand-50 p-5 max-w-[200px]">
                <Flame size={18} className="mb-2" />
                <p className="font-serif italic text-lg leading-snug">
                  "The most diverse food city in America."
                </p>
                <p className="mt-2 font-mono text-[9px] tracking-widest uppercase opacity-70">
                  — Bon Appétit
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TICKER */}
      <section className="bg-sand-900 text-sand-50 overflow-hidden py-5 border-y border-sand-900">
        <div className="ticker-marquee">
          {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
            <span key={i} className="font-serif italic text-2xl px-8 flex items-center gap-8 whitespace-nowrap">
              {t}
              <span className="text-brand-500 text-3xl">✦</span>
            </span>
          ))}
        </div>
      </section>

      {/* CITIES PILLAR STRIP */}
      <section className="bg-sand-50 border-b border-sand-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-5">
              <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-3">
                The Network
              </div>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-sand-900 leading-[1.05]">
                Eight markets.<br />One conversation about food.
              </h2>
            </div>
            <div className="lg:col-span-7 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-5">
              {[
                { c: "Houston", t: "Where it began" },
                { c: "Atlanta", t: "New Southern" },
                { c: "Dallas", t: "Texas, refined" },
                { c: "Los Angeles", t: "Pacific & beyond" },
                { c: "New Orleans", t: "Creole heritage" },
                { c: "New York", t: "Five boroughs" },
                { c: "San Diego", t: "Border to coast" },
                { c: "Golden Triangle", t: "Beaumont · Pt. Arthur · Orange" },
              ].map((p, i) => (
                <Link
                  key={p.c}
                  to={p.c === "More soon" ? "/directory" : `/directory`}
                  data-testid={`city-pillar-${p.c.toLowerCase().replace(/\s/g, "-")}`}
                  className="group block border-t-2 border-sand-900 pt-4 hover:border-brand-500 transition-colors animate-fade-up"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <div className="font-serif text-xl font-bold text-sand-900 group-hover:text-brand-500 transition-colors">
                    {p.c}
                  </div>
                  <div className="font-mono text-[10px] tracking-widest uppercase text-sand-600 mt-1">
                    {p.t}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED RESTAURANTS — Tetris grid with City Rotator */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 gap-4">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-3">
              The Directory · {city === "All" ? "All Cities" : city}
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-sand-900 leading-[1.05]">
              The Most<br />Talked-About Tables<br />
              {city === "All" ? "Across the Network." : `in ${city}.`}
            </h2>
          </div>
          <Link
            to="/directory"
            data-testid="see-all-restaurants"
            className="font-mono text-[11px] tracking-[0.25em] uppercase text-sand-900 editorial-link self-start md:self-end"
          >
            See All Restaurants →
          </Link>
        </div>

        {/* City Rotator */}
        <div className="flex flex-wrap gap-2.5 mb-12 border-y border-sand-200 py-5" data-testid="featured-city-rotator">
          {FEATURED_CITIES.map((c) => (
            <button
              key={c}
              data-testid={`featured-city-${c.toLowerCase().replace(/\s/g, "-")}`}
              onClick={() => setCity(c)}
              className={`px-5 py-2.5 font-mono text-[10px] tracking-[0.2em] uppercase border transition-all active:scale-95 ${
                city === c
                  ? "bg-brand-500 text-sand-50 border-brand-500"
                  : "bg-transparent text-sand-900 border-sand-300 hover:border-brand-500"
              }`}
            >
              {c === "All" ? "All Cities" : c}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="aspect-[4/5] bg-sand-100 animate-pulse" />
            ))}
          </div>
        ) : data.restaurants.length === 0 ? (
          <div className="py-16 text-center border border-dashed border-sand-300">
            <p className="font-serif italic text-2xl text-sand-700">No featured tables in {city} yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-16">
            {data.restaurants.slice(0, 6).map((r, i) => (
              <RestaurantCard key={r.id} r={r} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* FEATURED VIDEO — admin-curated, rotating */}
      <FeaturedVideo />

      {/* TRENDING — auto-scrolling strip by view count */}
      <TrendingStrip />

      {/* EDITORIAL — Hero article + side stack */}
      <section className="bg-white border-y border-sand-200 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 gap-4">
            <div>
              <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-3">
                The Editorial
              </div>
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-sand-900 leading-[1.05]">
                Stories from the<br />Kitchen, the Bar,<br />the Streets.
              </h2>
            </div>
            <Link to="/articles" data-testid="see-all-articles" className="font-mono text-[11px] tracking-[0.25em] uppercase text-sand-900 editorial-link self-start md:self-end">
              All Stories →
            </Link>
          </div>

          {heroArticle && (
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              <div className="lg:col-span-7">
                <ArticleCard a={heroArticle} variant="feature" index={0} />
              </div>
              <div className="lg:col-span-5 flex flex-col gap-10">
                {sideArticles.map((a, i) => (
                  <ArticleCard key={a.id} a={a} index={i + 1} />
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* EVENTS */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-32">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
          <div>
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-brand-500 mb-3">
              Signature Events
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold text-sand-900 leading-[1.05]">
              The Year, Plated.
            </h2>
          </div>
          <Link to="/events" data-testid="see-all-events" className="font-mono text-[11px] tracking-[0.25em] uppercase text-sand-900 editorial-link self-start md:self-end">
            Full Calendar →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {data.events.map((e, i) => (
            <EventCard key={e.id} e={e} index={i} />
          ))}
        </div>
      </section>

      {/* BUSINESS SERVICES CTA */}
      <section className="bg-sand-900 text-sand-50 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] tracking-[0.3em] uppercase text-ochre-400 mb-4">
              Business Services
            </div>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-[1.05]">
              We don't just<br />
              <span className="italic text-brand-300">cover</span> Houston food.<br />
              We <span className="text-brand-500">build</span> it.
            </h2>
            <p className="mt-6 text-sand-50/70 font-light text-lg max-w-xl leading-relaxed">
              Restaurant marketing retainers · Branding · Menu photography ·
              Commercial production · Grand opening campaigns · Social media ·
              Influencer collabs · PR · Recruitment.
            </p>
          </div>
          <div className="lg:col-span-5 lg:pl-10">
            <div className="border-l-4 border-brand-500 pl-8">
              <p className="font-serif italic text-2xl md:text-3xl leading-snug">
                "Tastebuds turned our grand opening into the most talked-about night of the year."
              </p>
              <p className="mt-4 font-mono text-[10px] tracking-widest uppercase text-ochre-400">
                — Owner, Featured Houston Restaurant
              </p>
            </div>
            <Link
              to="/services"
              data-testid="home-services-cta"
              className="mt-10 inline-block bg-brand-500 text-sand-50 px-8 py-4 font-mono text-[11px] font-bold tracking-[0.25em] uppercase hover:bg-brand-600 transition-colors active:scale-95"
            >
              Pitch Your Brand →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
